(() => {
  // Ignore calling same custom element
  if (customElements.get('product-card-slider')) return;

  class ProductCardSlider extends HTMLElement {
    constructor() {
      super();

      this.hasEverIntersected = false;
      this.instance = null;
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.intersectionObserver = null;
      this.onCardClick = this.onCardClick?.bind(this);
      this.cardWrapper = this.closest('.card-wrapper');
      this.onResize = this.onResize?.bind(this);
    }

    connectedCallback() {
      this.checkDeviceAndInit();

      this.addEventListener('click', this.onCardClick);

      // observe create/destroy outside viewport
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target !== this) continue;

            const visible =
              entry.isIntersecting || this.inViewportProximity(this, 1.5);

            if (visible) {
              this.hasEverIntersected = true;
              if (!this.instance) this.init();
            } else {
              if (this.hasEverIntersected) this.destroy();
            }
          }
        },
        { root: null, rootMargin: '150% 0px' }
      );

      this.intersectionObserver.observe(this);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      this.destroy();
      this.removeEventListener('click', this.onCardClick);

      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(this);
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      window.removeEventListener('resize', this.onResize);
    }

    init() {
      if (this.instance || !this.isConnected) return;

      const slidesCount = this.querySelectorAll('.swiper-slide').length;
      if (slidesCount <= 1) return;

      const scrollbarElement = this.ensureScrollbar();

      this.setAttribute('loading', 'true');

      this.instance = new Swiper(this, {
        preloadImages: false,
        lazy: {
          enabled: true,
          checkInView: true,
          loadPrevNext: 1,
          loadOnTransitionStart: true,
        },
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        autoHeight: false,
        wrapperClass: 'slider-wrapper',
        slideClass: 'swiper-slide',
        slidesPerView: 1,
        spaceBetween: 0,
        scrollbar: scrollbarElement
          ? {
              el: scrollbarElement,
              draggable: true,
              hide: false,
            }
          : undefined,
        on: {
          init: () => this.removeAttribute('loading'),
          imagesReady: () => this.removeAttribute('loading'),
          sliderMove: () => this.showScrollbar(),
          transitionStart: () => this.showScrollbar(),
          transitionEnd: () => this.hideScrollbar(),
        },
      });
    }

    checkDeviceAndInit() {
      const deviceSetting = (this.dataset.device || 'all').toLowerCase();

      if (deviceSetting !== 'all') {
        if (this.shouldEnable()) {
          this.classList.remove('hidden');
          this.cardWrapper
            ?.querySelector('.media-container')
            .classList.add('hidden');
          if (!this.instance) this.init();
        } else {
          this.classList.add('hidden');
          this.cardWrapper
            ?.querySelector('.media-container')
            .classList.remove('hidden');
          this.destroy();
        }
      } else {
        this.init();
      }
    }

    destroy() {
      if (!this.instance) return;

      this.instance.destroy(true, false);
      this.instance = null;

      if (this.swiper) {
        delete this.swiper;
      }
    }

    shouldEnable() {
      const deviceSetting = (this.dataset.device || 'all').toLowerCase();
      if (deviceSetting === 'mobile') return this.isMobile();
      if (deviceSetting === 'desktop') return !this.isMobile();
      return true;
    }

    // Events
    onCardClick(e) {
      const href = this.getAttribute('data-url');
      if (!href) return;

      const clickAllowed = !this.instance || this.instance.allowClick;
      if (!clickAllowed) return;

      if (e.metaKey || e.ctrlKey || e.button === 1) {
        window.open(href, '_blank');
      } else {
        window.location.assign(href);
      }
    }

    onResize() {
      const shouldEnable = this.shouldEnable();
      const currentlyEnabled = !!this.instance;

      if (shouldEnable && !currentlyEnabled) {
        this.checkDeviceAndInit();
      } else if (!shouldEnable && currentlyEnabled) {
        this.destroy();
        this.checkDeviceAndInit();
      }
    }

    // Scrollbar
    showScrollbar() {
      const scrollbar = this.querySelector('.swiper-scrollbar');
      if (!scrollbar) return;

      clearTimeout(this.scrollbarHideTimer);
      scrollbar.classList.add('is-visible');
    }

    hideScrollbar() {
      const scrollbar = this.querySelector('.swiper-scrollbar');
      if (!scrollbar) return;

      clearTimeout(this.scrollbarHideTimer);
      this.scrollbarHideTimer = setTimeout(() => {
        scrollbar.classList.remove('is-visible');
      }, 1000);
    }

    ensureScrollbar() {
      let scrollbar = this.querySelector('.swiper-scrollbar');
      if (!scrollbar) {
        scrollbar = document.createElement('div');
        scrollbar.className = 'swiper-scrollbar';
        this.appendChild(scrollbar);
      }
      return scrollbar;
    }

    // Utilities
    inViewportProximity = (el, mult = 2) => {
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 800;
      return rect.top < viewportHeight * mult && rect.bottom > -viewportHeight * mult;
    };

    isMobile() {
      return window.innerWidth < 768;
    }
  }

  customElements.define('product-card-slider', ProductCardSlider);
})();
